import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Tooltip,
  Snackbar,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  ContactMail as ContactIcon,
  Payment as PaymentIcon,
  Palette as BrandingIcon,
  CreditCard as SubscriptionIcon,
  CheckCircle as ActiveIcon,
  Cancel as CanceledIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  AttachMoney as MoneyIcon,
  Groups as GroupsIcon,
  School as CoachIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/environment';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { storage } from '../config/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const emptyInterclub = {
  name: '',
  shortName: '',
  description: '',
  brandingTitle: '',
  brandingDescription: '',
  logoUrl: '',
  contactEmail: '',
  contactPhone: '',
  contactWebsite: '',
  stripeAccountId: '',
  seasonEntryFee: '',
  isActive: true,
};

const emptyClub = {
  name: '',
  shortName: '',
  aliases: '',
  interclubId: '',
  locationCity: '',
  locationState: '',
  locationAddress: '',
  contactEmail: '',
  contactPhone: '',
  contactWebsite: '',
  isActive: true,
};

export default function InterclubManagement() {
  const { getIdToken } = useFirebaseAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [interclubDialogOpen, setInterclubDialogOpen] = useState(false);
  const [clubDialogOpen, setClubDialogOpen] = useState(false);
  const [editingInterclub, setEditingInterclub] = useState(null);
  const [editingClub, setEditingClub] = useState(null);
  const [interclubForm, setInterclubForm] = useState(emptyInterclub);
  const [clubForm, setClubForm] = useState(emptyClub);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState({});
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedInterclub, setSelectedInterclub] = useState(null);
  const [interclubStats, setInterclubStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');

  // Subscription status helper
  const getSubscriptionChip = (subscription) => {
    const status = subscription?.status || 'none';
    const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd;

    if (status === 'active' && cancelAtPeriodEnd) {
      return { label: 'Canceling', color: 'warning', icon: <WarningIcon fontSize="small" /> };
    }

    switch (status) {
      case 'active':
        return { label: 'Active', color: 'success', icon: <ActiveIcon fontSize="small" /> };
      case 'trialing':
        return { label: 'Trial', color: 'info', icon: <ActiveIcon fontSize="small" /> };
      case 'past_due':
        return { label: 'Past Due', color: 'error', icon: <WarningIcon fontSize="small" /> };
      case 'canceled':
        return { label: 'Canceled', color: 'default', icon: <CanceledIcon fontSize="small" /> };
      case 'unpaid':
        return { label: 'Unpaid', color: 'error', icon: <WarningIcon fontSize="small" /> };
      default:
        return { label: 'No Subscription', color: 'default', icon: null };
    }
  };

  // Subscription actions
  const handleSubscribe = async (interclubId) => {
    setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: true }));
    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ interclubId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Subscription checkout error:', err);
      setError(err.message || 'Failed to start subscription checkout');
    } finally {
      setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: false }));
    }
  };

  const handleCancelSubscription = async (interclubId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the billing period.')) {
      return;
    }

    setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: true }));
    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/subscriptions/${interclubId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      loadData();
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: false }));
    }
  };

  const handleReactivateSubscription = async (interclubId) => {
    setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: true }));
    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/subscriptions/${interclubId}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reactivate subscription');
      }

      loadData();
    } catch (err) {
      console.error('Reactivate subscription error:', err);
      setError(err.message || 'Failed to reactivate subscription');
    } finally {
      setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: false }));
    }
  };

  const handleManageSubscription = async (interclubId) => {
    setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: true }));
    try {
      const token = await getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/subscriptions/${interclubId}/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open billing portal');
      }

      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Manage subscription error:', err);
      setError(err.message || 'Failed to open billing portal');
    } finally {
      setSubscriptionLoading((prev) => ({ ...prev, [interclubId]: false }));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [interclubResponse, clubResponse, seasonsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/interclubs`).then((res) => res.json()),
        fetch(`${API_BASE_URL}/clubs`).then((res) => res.json()),
        fetch(`${API_BASE_URL}/seasons`).then((res) => res.json()),
      ]);
      setInterclubs(interclubResponse || []);
      setClubs(clubResponse || []);
      setSeasons(seasonsResponse || []);
      // Set current season as default
      const currentSeason = (seasonsResponse || []).find(s => s.isCurrent);
      if (currentSeason) {
        setSelectedSeasonId(currentSeason.id);
      }
    } catch (err) {
      console.error('Error loading interclub data:', err);
      setError('Failed to load interclub data');
    } finally {
      setLoading(false);
    }
  };

  const loadInterclubStats = async (interclubId, seasonId) => {
    try {
      setStatsLoading(true);
      const token = await getIdToken(true);
      const params = new URLSearchParams();
      if (seasonId) params.set('seasonId', seasonId);
      
      const response = await fetch(`${API_BASE_URL}/interclubs/${interclubId}/stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to load stats');
      }
      
      const stats = await response.json();
      setInterclubStats(stats);
    } catch (err) {
      console.error('Error loading interclub stats:', err);
      setError('Failed to load interclub stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleInterclubClick = (interclub) => {
    setSelectedInterclub(interclub);
    setStatsDialogOpen(true);
    loadInterclubStats(interclub.id, selectedSeasonId);
  };

  const handleSeasonChange = (seasonId) => {
    setSelectedSeasonId(seasonId);
    if (selectedInterclub) {
      loadInterclubStats(selectedInterclub.id, seasonId);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle subscription checkout return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subscriptionStatus = params.get('subscription');

    if (subscriptionStatus === 'success') {
      setSuccessMessage('Platform subscription activated successfully!');
      // Clean up URL
      navigate('/admin/interclubs', { replace: true });
    } else if (subscriptionStatus === 'canceled') {
      setError('Subscription checkout was canceled.');
      navigate('/admin/interclubs', { replace: true });
    }
  }, [location.search, navigate]);

  const handleInterclubSubmit = async () => {
    try {
      if (interclubForm.stripeAccountId && !interclubForm.stripeAccountId.trim().startsWith('acct_')) {
        throw new Error('Stripe Account ID must start with acct_ (not an email).');
      }
      const token = await getIdToken();
      if (!token) throw new Error('Missing auth token');

      const payload = {
        name: interclubForm.name,
        shortName: interclubForm.shortName,
        description: interclubForm.description,
        brandingTitle: interclubForm.brandingTitle,
        brandingDescription: interclubForm.brandingDescription,
        logoUrl: interclubForm.logoUrl,
        contact: {
          email: interclubForm.contactEmail,
          phone: interclubForm.contactPhone,
          website: interclubForm.contactWebsite,
        },
        stripeAccountId: interclubForm.stripeAccountId,
        seasonEntryFee: interclubForm.seasonEntryFee !== '' ? Number(interclubForm.seasonEntryFee) : null,
        isActive: interclubForm.isActive,
      };

      const url = editingInterclub
        ? `${API_BASE_URL}/interclubs/${editingInterclub.id}`
        : `${API_BASE_URL}/interclubs`;
      const method = editingInterclub ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save interclub');
      }

      setInterclubDialogOpen(false);
      setEditingInterclub(null);
      setInterclubForm(emptyInterclub);
      await loadData();
    } catch (err) {
      console.error('Error saving interclub:', err);
      setError(err.message || 'Failed to save interclub');
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    try {
      setLogoUploading(true);
      setLogoUploadError('');
      const safeName = file.name.replace(/[^a-z0-9.\-_]/gi, '_');
      const interclubId = editingInterclub?.id || 'new';
      const storageRef = ref(storage, `interclub-logos/${interclubId}/${Date.now()}-${safeName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      setInterclubForm((prev) => ({ ...prev, logoUrl: downloadUrl }));
    } catch (uploadError) {
      console.error('Logo upload failed:', uploadError);
      setLogoUploadError('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleClubSubmit = async () => {
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Missing auth token');

      const payload = {
        name: clubForm.name,
        shortName: clubForm.shortName,
        aliases: clubForm.aliases
          .split(',')
          .map((alias) => alias.trim())
          .filter(Boolean),
        interclubId: clubForm.interclubId,
        location: {
          city: clubForm.locationCity,
          state: clubForm.locationState,
          address: clubForm.locationAddress,
        },
        contact: {
          email: clubForm.contactEmail,
          phone: clubForm.contactPhone,
          website: clubForm.contactWebsite,
        },
        isActive: clubForm.isActive,
      };

      const url = editingClub ? `${API_BASE_URL}/clubs/${editingClub.id}` : `${API_BASE_URL}/clubs`;
      const method = editingClub ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save club');
      }

      setClubDialogOpen(false);
      setEditingClub(null);
      setClubForm(emptyClub);
      await loadData();
    } catch (err) {
      console.error('Error saving club:', err);
      setError(err.message || 'Failed to save club');
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Missing auth token');
      const endpoint = type === 'interclub' ? 'interclubs' : 'clubs';
      const response = await fetch(`${API_BASE_URL}/${endpoint}/${item.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      await loadData();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          🧭 Interclub Management
        </Typography>
        <IconButton onClick={loadData} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab label="Interclubs" />
        <Tab label="Clubs" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingInterclub(null);
                setInterclubForm(emptyInterclub);
                setInterclubDialogOpen(true);
              }}
            >
              Add Interclub
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Short Name</TableCell>
                  <TableCell>Platform Subscription</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {interclubs.map((interclub) => {
                  const chipConfig = getSubscriptionChip(interclub.subscription);
                  const isLoading = subscriptionLoading[interclub.id];
                  const hasActiveSubscription = ['active', 'trialing'].includes(interclub.subscription?.status);
                  const isCanceling = interclub.subscription?.cancelAtPeriodEnd;

                  return (
                  <TableRow 
                    key={interclub.id} 
                    hover 
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => handleInterclubClick(interclub)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {interclub.logoUrl ? (
                          <Box
                            component="img"
                            src={interclub.logoUrl}
                            alt={interclub.name}
                            sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: 1, 
                            bgcolor: 'primary.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          </Box>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{interclub.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{interclub.shortName || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={chipConfig.label}
                          color={chipConfig.color}
                          icon={chipConfig.icon}
                          sx={{ fontWeight: 500 }}
                        />
                        {isLoading ? (
                          <CircularProgress size={20} />
                        ) : hasActiveSubscription ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {isCanceling ? (
                              <Tooltip title="Reactivate subscription">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  onClick={(e) => { e.stopPropagation(); handleReactivateSubscription(interclub.id); }}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  Reactivate
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Cancel subscription">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={(e) => { e.stopPropagation(); handleCancelSubscription(interclub.id); }}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  Cancel
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip title="Manage billing">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleManageSubscription(interclub.id); }}
                              >
                                <SubscriptionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); handleSubscribe(interclub.id); }}
                            startIcon={<SubscriptionIcon />}
                            sx={{ ml: 1 }}
                          >
                            Subscribe
                          </Button>
                        )}
                        {interclub.subscription?.currentPeriodEnd && hasActiveSubscription && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {isCanceling ? 'Ends' : 'Renews'}{' '}
                            {new Date(
                              interclub.subscription.currentPeriodEnd._seconds
                                ? interclub.subscription.currentPeriodEnd._seconds * 1000
                                : interclub.subscription.currentPeriodEnd
                            ).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{interclub.isActive ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingInterclub(interclub);
                          setInterclubForm({
                            name: interclub.name || '',
                            shortName: interclub.shortName || '',
                            description: interclub.description || '',
                            brandingTitle: interclub.brandingTitle || '',
                            brandingDescription: interclub.brandingDescription || '',
                            logoUrl: interclub.logoUrl || '',
                            contactEmail: interclub.contact?.email || '',
                            contactPhone: interclub.contact?.phone || '',
                            contactWebsite: interclub.contact?.website || '',
                            stripeAccountId: interclub.stripeAccountId || '',
                            seasonEntryFee: interclub.seasonEntryFee ?? '',
                            isActive: interclub.isActive !== false,
                          });
                          setInterclubDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete('interclub', interclub); }}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingClub(null);
                setClubForm(emptyClub);
                setClubDialogOpen(true);
              }}
            >
              Add Club
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Interclub</TableCell>
                  <TableCell>Aliases</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clubs.map((club) => (
                  <TableRow key={club.id}>
                    <TableCell>{club.name}</TableCell>
                    <TableCell>
                      {interclubs.find((interclub) => interclub.id === club.interclubId)?.name || '-'}
                    </TableCell>
                    <TableCell>{(club.aliases || []).join(', ') || '-'}</TableCell>
                    <TableCell>{club.isActive ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingClub(club);
                          setClubForm({
                            name: club.name || '',
                            shortName: club.shortName || '',
                            aliases: (club.aliases || []).join(', '),
                            interclubId: club.interclubId || '',
                            locationCity: club.location?.city || '',
                            locationState: club.location?.state || '',
                            locationAddress: club.location?.address || '',
                            contactEmail: club.contact?.email || '',
                            contactPhone: club.contact?.phone || '',
                            contactWebsite: club.contact?.website || '',
                            isActive: club.isActive !== false,
                          });
                          setClubDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete('club', club)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Interclub Dialog */}
      <Dialog 
        open={interclubDialogOpen} 
        onClose={() => setInterclubDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 2, 
            bgcolor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BusinessIcon sx={{ color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingInterclub ? 'Edit Interclub' : 'Add Interclub'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editingInterclub ? 'Update interclub details' : 'Create a new interclub organization'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={interclubForm.name}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, name: e.target.value }))}
                size="small"
              />
              <TextField
                sx={{ minWidth: 140 }}
                label="Short Name"
                value={interclubForm.shortName}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, shortName: e.target.value }))}
                size="small"
              />
            </Box>
            <TextField
              fullWidth
              label="Description"
              value={interclubForm.description}
              onChange={(e) => setInterclubForm((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              size="small"
            />
          </Box>

          {/* Branding */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BrandingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                Branding
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Branding Title"
              value={interclubForm.brandingTitle}
              onChange={(e) => setInterclubForm((prev) => ({ ...prev, brandingTitle: e.target.value }))}
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Branding Description"
              value={interclubForm.brandingDescription}
              onChange={(e) => setInterclubForm((prev) => ({ ...prev, brandingDescription: e.target.value }))}
              multiline
              rows={2}
              size="small"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                label="Logo URL"
                value={interclubForm.logoUrl}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                placeholder="https://..."
                size="small"
              />
              <Button 
                variant="outlined" 
                component="label" 
                disabled={logoUploading}
                size="small"
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
              >
                {logoUploading ? 'Uploading...' : 'Upload'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                />
              </Button>
              {interclubForm.logoUrl && (
                <Box
                  component="img"
                  src={interclubForm.logoUrl}
                  alt="Logo preview"
                  sx={{ height: 40, width: 40, borderRadius: 1.5, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
                />
              )}
            </Box>
            {logoUploadError && (
              <Alert severity="error" sx={{ mt: 1 }} variant="outlined">
                {logoUploadError}
              </Alert>
            )}
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ContactIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                Contact Information
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Email"
                value={interclubForm.contactEmail}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
                size="small"
                type="email"
              />
              <TextField
                fullWidth
                label="Phone"
                value={interclubForm.contactPhone}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                size="small"
              />
            </Box>
            <TextField
              fullWidth
              label="Website"
              value={interclubForm.contactWebsite}
              onChange={(e) => setInterclubForm((prev) => ({ ...prev, contactWebsite: e.target.value }))}
              size="small"
              placeholder="https://..."
            />
          </Box>

          {/* Payment Settings */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PaymentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                Payment Settings
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Stripe Account ID"
                value={interclubForm.stripeAccountId}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, stripeAccountId: e.target.value }))}
                size="small"
                placeholder="acct_..."
                helperText="Use the Stripe account ID (acct_...)"
              />
              <TextField
                sx={{ minWidth: 140 }}
                label="Season Fee (USD)"
                value={interclubForm.seasonEntryFee}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, seasonEntryFee: e.target.value }))}
                size="small"
                type="number"
                inputProps={{ min: 0, step: 1 }}
              />
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={interclubForm.isActive}
                onChange={(e) => setInterclubForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                color="success"
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Active</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setInterclubDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleInterclubSubmit} 
            disabled={!interclubForm.name}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {editingInterclub ? 'Save Changes' : 'Create Interclub'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Club Dialog */}
      <Dialog 
        open={clubDialogOpen} 
        onClose={() => setClubDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 2, 
            bgcolor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LocationIcon sx={{ color: 'success.dark' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingClub ? 'Edit Club' : 'Add Club'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editingClub ? 'Update club details' : 'Create a new skating club'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={clubForm.name}
                onChange={(e) => setClubForm((prev) => ({ ...prev, name: e.target.value }))}
                size="small"
              />
              <TextField
                sx={{ minWidth: 140 }}
                label="Short Name"
                value={clubForm.shortName}
                onChange={(e) => setClubForm((prev) => ({ ...prev, shortName: e.target.value }))}
                size="small"
              />
            </Box>
            <TextField
              fullWidth
              label="Aliases"
              value={clubForm.aliases}
              onChange={(e) => setClubForm((prev) => ({ ...prev, aliases: e.target.value }))}
              helperText="Comma-separated alternate names (LTS, rink, etc.)"
              size="small"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Interclub</InputLabel>
              <Select
                value={clubForm.interclubId}
                label="Interclub"
                onChange={(e) => setClubForm((prev) => ({ ...prev, interclubId: e.target.value }))}
              >
                <MenuItem value="">
                  <em>Select interclub...</em>
                </MenuItem>
                {interclubs.map((interclub) => (
                  <MenuItem key={interclub.id} value={interclub.id}>
                    {interclub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Location */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                Location
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={clubForm.locationCity}
                onChange={(e) => setClubForm((prev) => ({ ...prev, locationCity: e.target.value }))}
                size="small"
              />
              <TextField
                sx={{ minWidth: 100 }}
                label="State"
                value={clubForm.locationState}
                onChange={(e) => setClubForm((prev) => ({ ...prev, locationState: e.target.value }))}
                size="small"
              />
            </Box>
            <TextField
              fullWidth
              label="Address"
              value={clubForm.locationAddress}
              onChange={(e) => setClubForm((prev) => ({ ...prev, locationAddress: e.target.value }))}
              size="small"
            />
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ContactIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                Contact Information
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Email"
                value={clubForm.contactEmail}
                onChange={(e) => setClubForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
                size="small"
                type="email"
              />
              <TextField
                fullWidth
                label="Phone"
                value={clubForm.contactPhone}
                onChange={(e) => setClubForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                size="small"
              />
            </Box>
            <TextField
              fullWidth
              label="Website"
              value={clubForm.contactWebsite}
              onChange={(e) => setClubForm((prev) => ({ ...prev, contactWebsite: e.target.value }))}
              size="small"
              placeholder="https://..."
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={clubForm.isActive}
                onChange={(e) => setClubForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                color="success"
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Active</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setClubDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleClubSubmit} 
            disabled={!clubForm.name || !clubForm.interclubId}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {editingClub ? 'Save Changes' : 'Create Club'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interclub Stats Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => {
          setStatsDialogOpen(false);
          setSelectedInterclub(null);
          setInterclubStats(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {selectedInterclub?.logoUrl ? (
              <Box
                component="img"
                src={selectedInterclub.logoUrl}
                alt={selectedInterclub?.name}
                sx={{ width: 48, height: 48, borderRadius: 2, objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BusinessIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedInterclub?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quick Stats & Overview
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => {
            setStatsDialogOpen(false);
            setSelectedInterclub(null);
            setInterclubStats(null);
          }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Season Selector */}
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Season</InputLabel>
              <Select
                value={selectedSeasonId}
                label="Season"
                onChange={(e) => handleSeasonChange(e.target.value)}
              >
                {seasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name} {season.isCurrent && '(Current)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {statsLoading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : interclubStats ? (
            <Grid container spacing={3}>
              {/* Clubs Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'primary.main',
                      display: 'flex'
                    }}>
                      <GroupsIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Clubs
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                    {interclubStats.clubs.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {interclubStats.clubs.active} active
                  </Typography>
                </Paper>
              </Grid>

              {/* Skaters Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(17, 153, 142, 0.08) 0%, rgba(56, 239, 125, 0.08) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'success.main',
                      display: 'flex'
                    }}>
                      <PeopleIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Registered Skaters
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
                    {interclubStats.skaters.total}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      {interclubStats.skaters.paid} paid
                    </Typography>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                      {interclubStats.skaters.unpaid} unpaid
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Revenue Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.08) 0%, rgba(255, 152, 0, 0.08) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'warning.main',
                      display: 'flex'
                    }}>
                      <MoneyIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Season Revenue
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.dark', mb: 0.5 }}>
                    ${interclubStats.revenue.collected.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${interclubStats.revenue.potential.toLocaleString()} potential @ ${interclubStats.revenue.entryFee}/skater
                  </Typography>
                </Paper>
              </Grid>

              {/* Competitions Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.08) 0%, rgba(156, 39, 176, 0.08) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'secondary.main',
                      display: 'flex'
                    }}>
                      <TrophyIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Competitions
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
                    {interclubStats.competitions.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {interclubStats.competitions.upcoming} upcoming
                  </Typography>
                </Paper>
              </Grid>

              {/* Users Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'info.main',
                      display: 'flex'
                    }}>
                      <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Total Users
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main', mb: 0.5 }}>
                    {interclubStats.users.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {interclubStats.users.parents} parents
                  </Typography>
                </Paper>
              </Grid>

              {/* User Breakdown Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5, 
                      bgcolor: 'grey.200',
                      display: 'flex'
                    }}>
                      <AdminIcon sx={{ color: 'grey.600', fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      User Breakdown
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Interclub Admins</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{interclubStats.users.interclubAdmins}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Club Admins</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{interclubStats.users.clubAdmins}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Coaches</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{interclubStats.users.coaches}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Parents</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{interclubStats.users.parents}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={() => {
              setStatsDialogOpen(false);
              setSelectedInterclub(null);
              setInterclubStats(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
